import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { OVERFLOW_GUTTER_PX } from '@/utils/appointmentOverlapLayout';

export type CalendarOverlapOverflowItem = {
  id: string;
  patient_name: string;
  service_name: string | null;
  starts_at: string;
  ends_at: string;
};

export function CalendarOverlapOverflow({
  appointments,
  top,
  height,
  language,
  onOpenAppointment,
}: {
  appointments: CalendarOverlapOverflowItem[];
  top: number;
  height: number;
  language: string;
  onOpenAppointment: (id: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const labelId = useId();
  const count = appointments.length;
  const formatter = new Intl.DateTimeFormat(language, {
    hour: '2-digit',
    minute: '2-digit',
  });

  useEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    const anchor = buttonRef.current?.getBoundingClientRect();
    if (!anchor) return;
    const width = 240;
    const left = Math.min(Math.max(8, anchor.right - width), window.innerWidth - width - 8);
    const topPx = Math.min(anchor.top, window.innerHeight - 16);
    setCoords({ top: topPx, left });
  }, [open]);

  useEffect(() => {
    if (!open) return;

    function handlePointer(event: MouseEvent) {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (buttonRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setOpen(false);
    }

    function handleKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        data-testid="calendar-overlap-overflow"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={t('appointments.card.overlap_more_label', { count })}
        className="pointer-events-auto absolute z-[4] flex items-center justify-center rounded-md border border-ds-line-strong bg-ds-sunk text-[10px] font-semibold leading-none text-ds-text shadow-sm hover:bg-ds-neutral-tint"
        style={{
          top: `${top}px`,
          height: `${Math.max(18, height)}px`,
          right: '2px',
          width: `${OVERFLOW_GUTTER_PX - 4}px`,
        }}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((current) => !current);
        }}
      >
        {t('appointments.card.overlap_more', { count })}
      </button>
      {open &&
        coords &&
        createPortal(
          <div
            ref={popoverRef}
            role="dialog"
            aria-labelledby={labelId}
            data-testid="calendar-overlap-overflow-list"
            className="fixed z-[70] max-h-64 overflow-auto rounded-xl border border-ds-line-strong bg-ds-card p-2 text-left shadow-pop"
            style={{ top: coords.top, left: coords.left, width: 240 }}
          >
            <p
              id={labelId}
              className="px-1.5 pb-1.5 text-[11px] font-medium text-ds-muted"
            >
              {t('appointments.card.overlap_hidden_title')}
            </p>
            <ul className="flex flex-col gap-0.5">
              {appointments.map((appointment) => {
                const start = new Date(appointment.starts_at);
                const end = new Date(appointment.ends_at);
                return (
                  <li key={appointment.id}>
                    <button
                      type="button"
                      data-testid="calendar-overlap-overflow-item"
                      data-appointment-id={appointment.id}
                      className="w-full rounded-md px-1.5 py-1.5 text-left hover:bg-ds-row-hover"
                      onClick={() => {
                        onOpenAppointment(appointment.id);
                        setOpen(false);
                      }}
                    >
                      <div className="truncate text-[11px] font-semibold text-ds-text">
                        {appointment.patient_name}
                      </div>
                      <div className="truncate text-[10px] text-ds-muted">
                        {formatter.format(start)} – {formatter.format(end)}
                        {appointment.service_name ? ` · ${appointment.service_name}` : ''}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>,
          document.body,
        )}
    </>
  );
}
